/**
 * pdf-cache.ts — offline backup of server-generated PDFs in the `pdfs` IDB store.
 *
 * Filled lazily: when a report's PDF is viewed online, its bytes are fetched once
 * and stored here so the técnico can still open it in the field without signal.
 * The authoritative PDF is always the server one; this is a read-only field cache.
 *
 * Fase 2b: added `pinned` and `lastAccessedAt` fields for LRU-aware retention.
 * - `pinned`: true = excluded from LRU eviction (Fase 3). Set by the user via
 *   "Guardar para offline" button.
 * - `lastAccessedAt`: ISO timestamp stamped on every getPdf() call. Used by Fase 3
 *   LRU eviction to find the least-recently-used record.
 *
 * Fase 3: LRU eviction. `enforcePdfCacheLimits()` caps the pdfs store at
 * PDF_CAP_COUNT entries OR PDF_CAP_BYTES total blob bytes (whichever is hit first).
 * Evicts the least-recently-used non-pinned records until both caps are satisfied.
 *
 * ponytail: LRU scan is a full getAll() — acceptable at N<=50 PDF records (small
 * set, no index needed). If the cap grows beyond 50, add a `by-lastAccessedAt`
 * index in idb.ts to avoid full-scan cost on eviction.
 */

import { getDb } from "../stores/db.js";

// ── Retention caps (Fase 3, §4) ───────────────────────────────────────────────

/** Maximum number of PDF records kept in the pdfs store. */
const PDF_CAP_COUNT = 50;
/** Maximum total blob size in bytes across all PDF records. */
const PDF_CAP_BYTES = 100 * 1024 * 1024; // 100 MB

const STORE = "pdfs";

interface PdfRecord {
  reportId: string;
  blob: Blob;
  updatedAt: string;
  /** Fase 2b: true = excluded from LRU eviction. Set by pin/unpin. */
  pinned?: boolean;
  /**
   * Fase 2b: ISO timestamp of the last time this PDF was accessed via getPdf().
   * Used by Fase 3 LRU eviction to choose the oldest-unused record to remove.
   */
  lastAccessedAt?: string;
}

/**
 * Store (upsert) a report's PDF bytes for offline access.
 * Preserves existing `pinned` and `lastAccessedAt` fields so a re-save
 * (e.g., when the server regenerates the PDF) does not silently unpin the record.
 *
 * Fase 3: after each write, enforces the LRU cap in the background (best-effort,
 * fire-and-forget — never throws to the caller).
 */
export async function savePdf(reportId: string, blob: Blob): Promise<void> {
  const db = await getDb();
  const existing = (await db.get(STORE, reportId)) as PdfRecord | undefined;
  await db.put(STORE, {
    ...(existing ?? {}),
    reportId,
    blob,
    updatedAt: new Date().toISOString(),
  } as PdfRecord);
  // Enforce retention cap after write — best-effort, never throws to caller.
  void enforcePdfCacheLimits().catch(() => {});
}

/**
 * Read a cached PDF blob, or undefined if none is stored.
 * Stamps `lastAccessedAt` on read for Fase 3 LRU tracking (best-effort, non-blocking).
 */
export async function getPdf(reportId: string): Promise<Blob | undefined> {
  const db = await getDb();
  const rec = (await db.get(STORE, reportId)) as PdfRecord | undefined;
  if (!rec) return undefined;
  // Stamp lastAccessedAt for LRU tracking. Best-effort fire-and-forget so callers
  // are not blocked by the write — eviction correctness at millisecond granularity
  // is not required.
  void db.put(STORE, { ...rec, lastAccessedAt: new Date().toISOString() });
  return rec.blob;
}

/**
 * Fetch a PDF from its URL and store it for offline use. Best-effort:
 * resolves false on any failure (offline, CORS, non-200) without throwing.
 */
export async function backupPdf(reportId: string, pdfUrl: string): Promise<boolean> {
  try {
    const res = await fetch(pdfUrl);
    if (!res.ok) return false;
    const blob = await res.blob();
    await savePdf(reportId, blob);
    return true;
  } catch {
    return false;
  }
}

/**
 * Pin a PDF so it is excluded from LRU eviction (Fase 3).
 * No-op if the record does not exist.
 */
export async function pinPdf(reportId: string): Promise<void> {
  const db = await getDb();
  const rec = (await db.get(STORE, reportId)) as PdfRecord | undefined;
  if (!rec) return;
  await db.put(STORE, { ...rec, pinned: true });
}

/**
 * Unpin a PDF, making it eligible for LRU eviction again.
 * Does NOT delete the blob — it remains cached but can be evicted by Fase 3 cleanup.
 * No-op if the record does not exist.
 */
export async function unpinPdf(reportId: string): Promise<void> {
  const db = await getDb();
  const rec = (await db.get(STORE, reportId)) as PdfRecord | undefined;
  if (!rec) return;
  await db.put(STORE, { ...rec, pinned: false });
}

/**
 * Returns true if a PDF record exists and is pinned.
 * Returns false if the record is absent or pinned is false/undefined.
 */
export async function isPdfPinned(reportId: string): Promise<boolean> {
  const db = await getDb();
  const rec = (await db.get(STORE, reportId)) as PdfRecord | undefined;
  return rec?.pinned === true;
}

// ── Fase 3: LRU retention enforcement ─────────────────────────────────────────

/**
 * Enforce PDF cache limits (§4, §4.1).
 *
 * Reads all records from the pdfs store and evicts the least-recently-used
 * non-pinned records until both caps are satisfied:
 *   - count  ≤ PDF_CAP_COUNT (50 files)
 *   - bytes  ≤ PDF_CAP_BYTES (100 MB)
 *
 * Eviction order: oldest `lastAccessedAt` first (falls back to `updatedAt` when
 * `lastAccessedAt` is absent — e.g., a PDF saved before Fase 2b).
 *
 * Pinned records are NEVER evicted.
 *
 * ponytail: scans the full pdfs store (fine at N≤50). If the cap grows, add a
 * `by-lastAccessedAt` index in idb.ts and use a cursor-based approach instead.
 */
export async function enforcePdfCacheLimits(): Promise<void> {
  const db = await getDb();
  const all = (await db.getAll(STORE)) as PdfRecord[];

  let totalCount = all.length;
  let totalBytes = all.reduce((acc, r) => acc + (r.blob?.size ?? 0), 0);

  // Fast path: both caps satisfied — nothing to do.
  if (totalCount <= PDF_CAP_COUNT && totalBytes <= PDF_CAP_BYTES) return;

  // Sort non-pinned records by LRU ascending (oldest access time first).
  const evictable = all
    .filter((r) => !r.pinned)
    .sort((a, b) => {
      const tA = a.lastAccessedAt ?? a.updatedAt;
      const tB = b.lastAccessedAt ?? b.updatedAt;
      return tA < tB ? -1 : tA > tB ? 1 : 0;
    });

  for (const record of evictable) {
    if (totalCount <= PDF_CAP_COUNT && totalBytes <= PDF_CAP_BYTES) break;
    await db.delete(STORE, record.reportId);
    totalCount--;
    totalBytes -= record.blob?.size ?? 0;
  }
}

/**
 * Best-effort startup prune (§4.1 — "limpieza al abrir con red").
 *
 * Runs `enforcePdfCacheLimits()` once at app startup. Uses
 * `navigator.storage.estimate()` to gauge quota pressure (informational only —
 * eviction runs regardless). Call this near other startup cleanup (e.g., alongside
 * `cleanupGhostReports`) from TechnicianPanel's mount effect.
 *
 * Never throws — any error is silently swallowed so it cannot break startup.
 */
export async function pruneOnStartup(): Promise<void> {
  try {
    await enforcePdfCacheLimits();
  } catch {
    // Best-effort — never propagate to callers.
  }
}
